import React, { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import { DefaultInput } from "src/components";
import { ButtonAddOutlined } from "src/components/Button/ButtonAddOutlined";
import { SubmitButton } from "src/components/Button/SubmitButton";
import { AvatarSelect } from "src/components/ImageSelect/AvatarSelect";
import { SectionHeader } from "src/components/SideBar/CalendarSideBar/components/CreateScheduleMenu/components/Popup/components";
import { dummyPic } from "src/constants/services";
import { PlusIcon } from "src/Icons";
import { createCategories, getCategories } from "src/store/reducers/categoriesSlice";
import { createCategoryTemplate } from "src/store/reducers/categoryTemplatesSlice";
import { closeModal, showModal } from "src/store/reducers/modalSlice";
import { isTitle } from "src/utils/validation";

export enum TypeOfServicesModalType {
  CreateServices,
  AddSubCategory,
  EditCategory,
  DeleteSubCategory,
}

interface CreateCategorySectionProps {
  studioId?: string;
  onClose: () => void;
  onSubmit: () => void;
  validator: (image: string, categoryName: string, subcategoryNames: Array<any>) => boolean;
}

export const CreateCategorySection = ({
  studioId,
  onClose,
  onSubmit,
  validator,
}: CreateCategorySectionProps) => {
  const dispatch = useDispatch();

  const [image, setImage] = useState<string>("");

  const [title, setTitle] = useState<string>("");
  const [arraySubCategories, setArraySubCategories] = useState<any[]>([]);

  const [isShowSubCategoryInput, setIsShowSubCategoryInput] =
    useState<boolean>(false);

  const formRef = useRef<any>();
  const avatarRef = useRef<any>();

  const handleChangeImage = (newImage: string) => {
    setImage(newImage);
  };

  const handleChangeCategoryName = (newValue: string) => {
    setTitle(newValue);
  };

  const handleChangeSubcategoryName = useCallback((newValue: string, id: number) => {
    setArraySubCategories(
      arraySubCategories &&
        arraySubCategories.map((subCategory: any) =>
          +subCategory.id === id
            ? { ...subCategory, title: newValue }
            : subCategory
        )
    );
  }, [arraySubCategories]);

  const resetModalData = () => {
    setImage("");
    setTitle("");
    setArraySubCategories([]);
    setIsShowSubCategoryInput(false);
  };

  const onCloseModal = () => {
    onSubmit();
    resetModalData();
    onClose();
  };

  const AddSubcategoryInput = useCallback(() => {
    const categoryFirstChildStyles = {
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
    };
    const categoryLastChildStyles = {
      borderBottomLeftRadius: "6px",
      borderBottomRightRadius: "6px",
    };
    return (
      isShowSubCategoryInput && (
        <Box
          sx={{
            marginTop: "32px",
          }}
        >
          <SectionHeader label="Add subcategory" />
          <Box>
            {arraySubCategories &&
              arraySubCategories.map((subCategory: any, index: number) => (
                <DefaultInput
                  key={+subCategory.id}
                  placeholder="subcategory"
                  initialValue={subCategory.title}
                  label={
                    arraySubCategories.length > 1
                      ? `Subcategory ${index + 1}`
                      : "Subcategory"
                  }
                  onInputValueChange={(newValue: string) =>
                    handleChangeSubcategoryName(newValue, +subCategory.id)
                  }
                  validationFn={isTitle}
                  sx={{
                    "&:first-of-type": categoryFirstChildStyles,
                    "&:last-of-type": categoryLastChildStyles,
                  }}
                />
              ))}
          </Box>
        </Box>
      )
    );
  }, [arraySubCategories, handleChangeSubcategoryName, isShowSubCategoryInput]);

  const handleAddSubcategory = () => {
    setIsShowSubCategoryInput(true);
    setArraySubCategories([
      ...arraySubCategories,
      {
        id: `${arraySubCategories.length + 1}`,
        title: "",
      },
    ]);
  };

  const handleSaveButtonPressConditionaly = () => {

    const action = studioId ? createCategories : createCategoryTemplate
    const conditionalParams = studioId ? { studioId } : {};

    dispatch(
      action({
        ...conditionalParams,
        title,
        image,
        onSuccess: (response: any) => {
          if (arraySubCategories.length >= 1) {
            const subCategories = arraySubCategories.map(({ title, image }: any) => ({
              title,
              image,
              parentId: response.id,
            }));

            const promises = subCategories.map(({ title, parentId , image}: any) => {
              return new Promise((res, rej) => {
                dispatch(
                  action({
                    ...conditionalParams,
                    title,
                    image: image || dummyPic,
                    parentId,
                    onSuccess: () => {
                      res(title);
                    },
                    onFailure: () => {
                      rej();
                    },
                  })
                );
              });
            });

            Promise.all(promises)
              .then(() => {
                dispatch(getCategories());
                onCloseModal();
                dispatch(
                  showModal({
                    modalType: 'SUCCESS',
                    successText: `Category and subcategories successfully ${studioId ? "assigned" : "created"}`,
                    okButtonTitle: 'Ok, I got it',
                    onButtonClick: () => {
                      dispatch(closeModal());
                    },
                  })
                );
              })
              .catch(() => {
                dispatch(
                  showModal({
                    modalType: 'DELETE',
                    title: `${"Subcategories can`t be added"}`,
                    okButtonTitle: 'Ok, I got it',
                    isButtonCancel: true,
                    onButtonClick: () => {
                      dispatch(closeModal());
                    },
                  })
                );
                onCloseModal();
              });
          } else {
            dispatch(getCategories());
            onCloseModal();
            dispatch(
              showModal({
                modalType: 'SUCCESS',
                successText: `Category successfully ${studioId ? "assigned" : "created"}`,
                okButtonTitle: 'Ok, I got it',
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          }
        },
        onFailure: (error: any) => {
          dispatch(
            showModal({
              modalType: 'DELETE',
              title: `${error?.response?.data?.detail || error.message}`,
              okButtonTitle: 'Ok, I got it',
              isButtonCancel: true,
              onButtonClick: () => {
                dispatch(closeModal());
              },
            })
          );
        },
      })
    );
  }

  return (
    <>
      <Box
        sx={{
          margin: "32px 0 0",
        }}
      >
        <AvatarSelect
          ref={avatarRef}
          image={image}
          onImageChanged={handleChangeImage}
          sx={{
            width: 88,
            height: 88,
          }}
        />
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#828282",
            marginTop: "16px",
            marginBottom: "30px",
          }}
        >
          Allowed file types
          : png, jpg, jpeg.
        </Typography>
      </Box>

      <Box
        sx={{
          marginBottom: "16px",
        }}
      >
        <form ref={formRef}>
          <DefaultInput
            initialValue={title}
            label="Category name"
            placeholder="category"
            onInputValueChange={handleChangeCategoryName}
            validationFn={isTitle}
            sx={{
              borderTopRightRadius: "6px",
              borderTopLeftRadius: "6px",
              borderBottomRightRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          />
          {AddSubcategoryInput()}
          <ButtonAddOutlined
            title="Add subcategory"
            onClick={handleAddSubcategory}
            leftIcon={<PlusIcon fontSize="inherit" />}
          />
        </form>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <SubmitButton
          variant="outlined"
          title="Cancel"
          onClick={onCloseModal}
          sx={{
            width: "276px",
            borderRadius: "6px",
          }}
        />
        <SubmitButton
          title="Create category"
          disabled={!validator(image, title, arraySubCategories.map((sc: { title: any; }) => sc.title))}
          onClick={handleSaveButtonPressConditionaly}
          sx={{
            width: "276px",
            borderRadius: "6px",
          }}
        />
      </Box>
    </>
  );
};
